import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AuthLayout from "@/components/layout/AuthLayout";
import { Loader2, CheckCircle2 } from "lucide-react";

const ROLE_LABELS = { owner: "Propriétaire", manager: "Manager", reception: "Réception", guide: "Guide", accountant: "Comptable" };

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Lien invalide."); return; }
    api.get(`/api/invitations/info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Invitation invalide."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/api/invitations/accept", { token, name: name || undefined, password: password || undefined });
      setDone(true);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || er.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Rejoindre l'équipe</h1>
      {err && !info && <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="invite-error">{err}</div>}
      {done ? (
        <div className="mt-8 space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="invite-success">
            <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-3 text-sm text-emerald-800">Votre accès a été créé. Connectez-vous pour commencer.</p>
          </div>
          <button onClick={() => nav("/login")} className="flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700" data-testid="invite-go-login">Se connecter</button>
        </div>
      ) : info ? (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
          <p className="text-sm text-slate-600">Vous êtes invité à rejoindre <strong>{info.organization_name}</strong> en tant que <strong>{ROLE_LABELS[info.role] || info.role}</strong> ({info.email}).</p>
          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="invite-form-error">{err}</div>}
          {info.needs_account && (<>
            <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Votre nom</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" /></div>
            <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Mot de passe</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" placeholder="8 caractères minimum" className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" /></div>
          </>)}
          {!info.needs_account && <p className="text-xs text-slate-400">Vous avez déjà un compte ActiPilote : acceptez pour rejoindre cette organisation.</p>}
          <button type="submit" disabled={busy} data-testid="invite-submit-button" className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Rejoindre {info.organization_name}
          </button>
        </form>
      ) : !err ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : null}
      <p className="mt-6 text-center text-sm text-slate-500"><Link to="/login" className="font-medium text-primary hover:underline">Retour à la connexion</Link></p>
    </AuthLayout>
  );
}
